import { getMomentTz, TimeService } from "@dexteel/mesf-core";
import { useCallback, useMemo, useRef, useState } from "react";
import { Event, EventSegment } from "../models/Event";
import { calculateNewEventDates } from "../utils/calendarUtils";

const moment = getMomentTz();

const DRAG_THRESHOLD = 5;

interface DraggedEventInfo {
	event: Event;
	segment: EventSegment;
	offsetX: number;
	offsetY: number;
	elementRect: DOMRect | null;
}

interface PendingDragInfo {
	segment: EventSegment;
	startX: number;
	startY: number;
	isDragActivated: boolean;
}

interface DropLineInfo {
	x: number;
	y: number;
	height: number;
	position: "before" | "after" | "empty";
	targetEventId?: number;
	targetHour?: number;
	useStartInsteadOfEnd?: boolean; // When true, use START of target event (for first half)
}

export const useEventDragDrop = ({
	editable,
	onEventDrop,
	eventSegments,
}: {
	editable: boolean;
	onEventDrop: (eventId: number, newStartDate: Date, newEndDate: Date) => void;
	eventSegments: EventSegment[];
}) => {
	const draggedEventRef = useRef<DraggedEventInfo | null>(null);
	const pendingDragRef = useRef<PendingDragInfo | null>(null);
	const dragListenerRef = useRef<((e: MouseEvent) => void) | null>(null);
	const dragEndListenerRef = useRef<((e: MouseEvent) => void) | null>(null);
	const escapeListenerRef = useRef<((e: KeyboardEvent) => void) | null>(null);
	const dropLineInfoRef = useRef<DropLineInfo | null>(null);
	const shiftKeyPressedRef = useRef<boolean>(false);

	const [dragPosition, setDragPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [hoveredDropDate, setHoveredDropDate] = useState<Date | null>(null);
	const [dropLineInfo, setDropLineInfo] = useState<DropLineInfo | null>(null);
	const [isFreeDragMode, setIsFreeDragMode] = useState<boolean>(false);

	const updateDropLineInfo = useCallback((info: DropLineInfo | null) => {
		setDropLineInfo(info);
		dropLineInfoRef.current = info;
	}, []);

	const getEventElements = useCallback((eventId: number): HTMLElement[] => {
		return Array.from(
			document.querySelectorAll(`[data-event-id="${eventId}"]`),
		) as HTMLElement[];
	}, []);

	const calculateHourFromPosition = useCallback(
		(clientX: number, dayCellRect: DOMRect): number => {
			const relativeX = clientX - dayCellRect.left;
			const cellWidth = dayCellRect.width;
			const percentage = Math.max(0, Math.min(1, relativeX / cellWidth));
			const exactHour = percentage * 24;
			const roundedHour = Math.round(exactHour);
			return Math.min(23, Math.max(0, roundedHour));
		},
		[],
	);

	const calculateDropLinePosition = useCallback(
		(clientX: number, clientY: number, freeMode: boolean = false) => {
			if (!draggedEventRef.current) return;

			if (freeMode) {
				let dayCell: HTMLElement | null = null;
				let currentElement = document.elementFromPoint(
					clientX,
					clientY,
				) as HTMLElement;

				while (currentElement && !dayCell) {
					if (currentElement.hasAttribute("data-date")) {
						dayCell = currentElement;
						break;
					}
					currentElement = currentElement.parentElement as HTMLElement;
				}

				if (dayCell) {
					const dayCellRect = dayCell.getBoundingClientRect();
					const targetHour = calculateHourFromPosition(clientX, dayCellRect);
					updateDropLineInfo({
						x: clientX,
						y: dayCellRect.top,
						height: dayCellRect.height,
						position: "empty",
						targetHour,
					});
				} else {
					updateDropLineInfo(null);
				}
				return;
			}

			let dayCell: HTMLElement | null = null;
			let currentElement = document.elementFromPoint(
				clientX,
				clientY,
			) as HTMLElement;

			while (currentElement && !dayCell) {
				if (currentElement.hasAttribute("data-date")) {
					dayCell = currentElement;
					break;
				}
				currentElement = currentElement.parentElement as HTMLElement;
			}

			if (!dayCell) {
				updateDropLineInfo(null);
				return;
			}

			const dayCellRect = dayCell.getBoundingClientRect();
			const dateStr = dayCell.getAttribute("data-date");

			if (!dateStr) {
				updateDropLineInfo(null);
				return;
			}

			const allVisibleSegments = eventSegments.filter((segment) => {
				if (
					draggedEventRef.current &&
					segment.event.EventId === draggedEventRef.current.event.EventId
				) {
					return false;
				}
				return true;
			});

			const serverTz = TimeService.getInstance().getServerTimeZone();
			const segmentsInDay = allVisibleSegments.filter((segment) => {
				const segStart = moment.tz(segment.segStart, serverTz);
				const segEnd = moment.tz(segment.segEnd, serverTz);
				const currentDay = moment.tz(dateStr, "YYYY-MM-DD", serverTz);
				return (
					segStart.isSameOrBefore(currentDay, "day") &&
					segEnd.isSameOrAfter(currentDay, "day")
				);
			});

			// CASE 1: No events on that day
			if (segmentsInDay.length === 0) {
				const targetHour = calculateHourFromPosition(clientX, dayCellRect);
				updateDropLineInfo({
					x: clientX,
					y: dayCellRect.top,
					height: dayCellRect.height,
					position: "empty",
					targetHour,
				});
				return;
			}

			// CASE 2 y 3: Search if the cursor its on an event
			let targetSegment: EventSegment | null = null;
			let segmentStartX: number | null = null;
			let segmentEndX: number | null = null;
			let isFirstHalf = true;

			for (const segment of allVisibleSegments) {
				const eventElements = getEventElements(segment.event.EventId);
				if (eventElements.length === 0) continue;

				let hoveredElement: HTMLElement | null = null;

				for (const element of eventElements) {
					const rect = element.getBoundingClientRect();
					const margin = 5;
					if (
						clientX >= rect.left - margin &&
						clientX <= rect.right + margin &&
						clientY >= rect.top - margin &&
						clientY <= rect.bottom + margin
					) {
						hoveredElement = element;
						break;
					}
				}

				if (hoveredElement) {
					const hoveredRect = hoveredElement.getBoundingClientRect();
					const verticalTolerance = 5;
					const horizontalGapTolerance = 10;
					const elementsInSameRow: { element: HTMLElement; rect: DOMRect }[] =
						[];

					for (const element of eventElements) {
						const rect = element.getBoundingClientRect();
						if (Math.abs(rect.top - hoveredRect.top) <= verticalTolerance) {
							elementsInSameRow.push({ element, rect });
						}
					}

					elementsInSameRow.sort((a, b) => a.rect.left - b.rect.left);

					let groupStart = hoveredRect.left;
					let groupEnd = hoveredRect.right;

					for (let i = elementsInSameRow.length - 1; i >= 0; i--) {
						const { rect } = elementsInSameRow[i];
						if (rect.right >= groupStart - horizontalGapTolerance) {
							groupStart = Math.min(groupStart, rect.left);
						} else {
							break;
						}
					}

					for (let i = 0; i < elementsInSameRow.length; i++) {
						const { rect } = elementsInSameRow[i];
						if (rect.left <= groupEnd + horizontalGapTolerance) {
							groupEnd = Math.max(groupEnd, rect.right);
						} else {
							break;
						}
					}

					targetSegment = segment;
					segmentStartX = groupStart;
					segmentEndX = groupEnd;

					const totalWidth = groupEnd - groupStart;
					const segmentMiddleX = groupStart + totalWidth / 2;
					isFirstHalf = clientX < segmentMiddleX;
					break;
				}
			}

			if (targetSegment && segmentStartX !== null && segmentEndX !== null) {
				// Always use "after" position to prioritize START of dragged event
				// First half: dragged event STARTS at existing event's START
				// Second half: dragged event STARTS at existing event's END
				if (isFirstHalf) {
					updateDropLineInfo({
						x: segmentStartX,
						y: dayCellRect.top,
						height: dayCellRect.height,
						position: "after",
						targetEventId: targetSegment.event.EventId,
						useStartInsteadOfEnd: true,
					});
				} else {
					updateDropLineInfo({
						x: segmentEndX,
						y: dayCellRect.top,
						height: dayCellRect.height,
						position: "after",
						targetEventId: targetSegment.event.EventId,
						useStartInsteadOfEnd: false,
					});
				}
			}
			// CASE 4: There are events but the cursor isnt on one
			else if (segmentsInDay.length > 0) {
				const eventPositions: Array<{
					segment: EventSegment;
					left: number;
					right: number;
				}> = [];

				for (const segment of segmentsInDay) {
					const eventElements = getEventElements(segment.event.EventId);
					const verticalMargin = 50;
					let referenceElement: HTMLElement | null = null;

					for (const element of eventElements) {
						const rect = element.getBoundingClientRect();
						if (
							clientY >= rect.top - verticalMargin &&
							clientY <= rect.bottom + verticalMargin
						) {
							referenceElement = element;
							break;
						}
					}

					if (referenceElement) {
						const refRect = referenceElement.getBoundingClientRect();
						const verticalTolerance = 5;
						const horizontalGapTolerance = 10;
						const elementsInSameRow: { element: HTMLElement; rect: DOMRect }[] =
							[];

						for (const element of eventElements) {
							const rect = element.getBoundingClientRect();
							if (Math.abs(rect.top - refRect.top) <= verticalTolerance) {
								elementsInSameRow.push({ element, rect });
							}
						}

						elementsInSameRow.sort((a, b) => a.rect.left - b.rect.left);

						if (elementsInSameRow.length > 0) {
							let groupStart = elementsInSameRow[0].rect.left;
							let groupEnd = elementsInSameRow[0].rect.right;

							for (let i = 1; i < elementsInSameRow.length; i++) {
								const { rect } = elementsInSameRow[i];
								if (rect.left <= groupEnd + horizontalGapTolerance) {
									groupEnd = Math.max(groupEnd, rect.right);
								} else {
									break;
								}
							}

							eventPositions.push({
								segment,
								left: groupStart,
								right: groupEnd,
							});
						}
					}
				}

				eventPositions.sort((a, b) => a.left - b.left);

				if (eventPositions.length > 0) {
					const firstEvent = eventPositions[0];
					const lastEvent = eventPositions[eventPositions.length - 1];

					// Find the closest event to the cursor
					let closestEvent = firstEvent;
					let minDistance = Math.abs(clientX - firstEvent.right);

					for (const eventPos of eventPositions) {
						const distToRight = Math.abs(clientX - eventPos.right);
						if (distToRight < minDistance) {
							minDistance = distToRight;
							closestEvent = eventPos;
						}
					}

					// Always use "after" position (dragged event starts where existing event ends)
					updateDropLineInfo({
						x: closestEvent.right,
						y: dayCellRect.top,
						height: dayCellRect.height,
						position: "after",
						targetEventId: closestEvent.segment.event.EventId,
					});
				} else {
					const targetHour = calculateHourFromPosition(clientX, dayCellRect);
					updateDropLineInfo({
						x: clientX,
						y: dayCellRect.top,
						height: dayCellRect.height,
						position: "empty",
						targetHour,
					});
				}
			} else {
				const targetHour = calculateHourFromPosition(clientX, dayCellRect);
				updateDropLineInfo({
					x: clientX,
					y: dayCellRect.top,
					height: dayCellRect.height,
					position: "empty",
					targetHour,
				});
			}
		},
		[
			eventSegments,
			getEventElements,
			calculateHourFromPosition,
			updateDropLineInfo,
		],
	);

	const handleDayDropInternal = useCallback(
		(targetDate: Date, targetHour?: number) => {
			if (!draggedEventRef.current || !editable) return;

			const { event, segment } = draggedEventRef.current;

			const newDates = calculateNewEventDates(
				segment.originalStart,
				segment.originalEnd,
				targetDate,
			);

			if (newDates) {
				const serverTz = TimeService.getInstance().getServerTimeZone();

				if (targetHour !== undefined) {
					const duration = newDates.end.getTime() - newDates.start.getTime();
					const startMoment = moment.tz(newDates.start, serverTz);
					startMoment.hours(targetHour).minutes(0).seconds(0).milliseconds(0);
					const adjustedStart = startMoment.toDate();
					const adjustedEnd = new Date(adjustedStart.getTime() + duration);
					onEventDrop(event.EventId, adjustedStart, adjustedEnd);
				} else {
					onEventDrop(event.EventId, newDates.start, newDates.end);
				}
			}

			handleDragEndInternal();
		},
		[editable, onEventDrop],
	);

	const handleDayDropInternalWithTime = useCallback(
		(targetDateTime: Date, anchorPoint: "start" | "end") => {
			if (!draggedEventRef.current || !editable) return;

			const { event, segment } = draggedEventRef.current;

			const duration =
				segment.originalEnd.getTime() - segment.originalStart.getTime();

			let newStart: Date;
			let newEnd: Date;

			if (anchorPoint === "end") {
				newEnd = targetDateTime;
				newStart = new Date(newEnd.getTime() - duration);
			} else {
				newStart = targetDateTime;
				newEnd = new Date(newStart.getTime() + duration);
			}

			onEventDrop(event.EventId, newStart, newEnd);
			handleDragEndInternal();
		},
		[editable, onEventDrop],
	);

	const activateDrag = useCallback(
		(segment: EventSegment, startX: number, startY: number) => {
			const element = document.querySelector(
				`[data-event-id="${segment.event.EventId}"]`,
			) as HTMLElement;

			if (!element) return;

			const rect = element.getBoundingClientRect();

			draggedEventRef.current = {
				event: segment.event,
				segment,
				offsetX: startX - rect.left,
				offsetY: startY - rect.top,
				elementRect: rect,
			};

			setDragPosition({
				x: startX,
				y: startY,
			});
			calculateDropLinePosition(startX, startY);

			if (!dragListenerRef.current) {
				dragListenerRef.current = (moveEvent: MouseEvent) => {
					const isFreeMode = moveEvent.shiftKey;
					shiftKeyPressedRef.current = isFreeMode;
					setIsFreeDragMode(isFreeMode);

					setDragPosition({
						x: moveEvent.clientX,
						y: moveEvent.clientY,
					});
					calculateDropLinePosition(
						moveEvent.clientX,
						moveEvent.clientY,
						isFreeMode,
					);
				};
				document.addEventListener("mousemove", dragListenerRef.current);
			}

			// Add ESC key listener to cancel drag
			if (!escapeListenerRef.current) {
				escapeListenerRef.current = (keyEvent: KeyboardEvent) => {
					if (keyEvent.key === "Escape") {
						console.log("=== DRAG CANCELLED BY ESC ===");
						handleDragEndInternal();
					}
				};
				document.addEventListener("keydown", escapeListenerRef.current);
			}

			if (!dragEndListenerRef.current) {
				dragEndListenerRef.current = (upEvent: MouseEvent) => {
					const element = document.elementFromPoint(
						upEvent.clientX,
						upEvent.clientY,
					) as HTMLElement;

					let dayElement = element;
					while (dayElement && !dayElement.hasAttribute("data-date")) {
						dayElement = dayElement.parentElement as HTMLElement;
					}

					if (dayElement && draggedEventRef.current) {
						const dateStr = dayElement.getAttribute("data-date");

						if (dateStr) {
							try {
								const serverTz = TimeService.getInstance().getServerTimeZone();
								const currentDropLineInfo = dropLineInfoRef.current;

								if (currentDropLineInfo) {
									if (
										currentDropLineInfo.position === "before" &&
										currentDropLineInfo.targetEventId
									) {
										const targetSegment = eventSegments.find(
											(seg) =>
												seg.event.EventId === currentDropLineInfo.targetEventId,
										);

										if (targetSegment) {
											const targetStartMoment = moment.tz(
												targetSegment.segStart,
												serverTz,
											);
											handleDayDropInternalWithTime(
												targetStartMoment.toDate(),
												"end",
											);
										} else {
											const targetMoment = moment
												.tz(dateStr, "YYYY-MM-DD", serverTz)
												.startOf("day");
											handleDayDropInternal(targetMoment.toDate(), undefined);
										}
									} else if (
										currentDropLineInfo.position === "after" &&
										currentDropLineInfo.targetEventId
									) {
										const targetSegment = eventSegments.find(
											(seg) =>
												seg.event.EventId === currentDropLineInfo.targetEventId,
										);

										if (targetSegment) {
											// Check if we should use START or END of target event
											const useStart =
												currentDropLineInfo.useStartInsteadOfEnd ?? false;
											const targetMoment = useStart
												? moment.tz(targetSegment.segStart, serverTz)
												: moment.tz(targetSegment.segEnd, serverTz);

											handleDayDropInternalWithTime(
												targetMoment.toDate(),
												"start",
											);
										} else {
											const targetMoment = moment
												.tz(dateStr, "YYYY-MM-DD", serverTz)
												.startOf("day");
											handleDayDropInternal(targetMoment.toDate(), undefined);
										}
									} else if (currentDropLineInfo.position === "empty") {
										const targetMoment = moment
											.tz(dateStr, "YYYY-MM-DD", serverTz)
											.startOf("day");
										const targetHour = currentDropLineInfo.targetHour;
										handleDayDropInternal(targetMoment.toDate(), targetHour);
									} else {
										const targetMoment = moment
											.tz(dateStr, "YYYY-MM-DD", serverTz)
											.startOf("day");
										handleDayDropInternal(targetMoment.toDate(), undefined);
									}
								} else {
									const targetMoment = moment
										.tz(dateStr, "YYYY-MM-DD", serverTz)
										.startOf("day");

									const allVisibleSegments = eventSegments.filter((segment) => {
										if (
											draggedEventRef.current &&
											segment.event.EventId ===
												draggedEventRef.current.event.EventId
										) {
											return false;
										}
										return true;
									});

									let isOverEvent = false;
									for (const segment of allVisibleSegments) {
										const eventElements = getEventElements(
											segment.event.EventId,
										);

										for (const elem of eventElements) {
											const rect = elem.getBoundingClientRect();
											const margin = 5;

											if (
												upEvent.clientX >= rect.left - margin &&
												upEvent.clientX <= rect.right + margin &&
												upEvent.clientY >= rect.top - margin &&
												upEvent.clientY <= rect.bottom + margin
											) {
												isOverEvent = true;
												break;
											}
										}

										if (isOverEvent) break;
									}

									let targetHour: number | undefined = undefined;
									if (!isOverEvent) {
										const dayCellRect = dayElement.getBoundingClientRect();
										const relativeX = upEvent.clientX - dayCellRect.left;
										const cellWidth = dayCellRect.width;
										const percentage = Math.max(
											0,
											Math.min(1, relativeX / cellWidth),
										);
										const exactHour = percentage * 24;
										targetHour = Math.min(
											23,
											Math.max(0, Math.round(exactHour)),
										);
									}

									handleDayDropInternal(targetMoment.toDate(), targetHour);
								}
							} catch {
								handleDragEndInternal();
							}
						}
					} else {
						handleDragEndInternal();
					}
				};
				document.addEventListener("mouseup", dragEndListenerRef.current);
			}
		},
		[
			editable,
			calculateDropLinePosition,
			eventSegments,
			getEventElements,
			handleDayDropInternal,
			handleDayDropInternalWithTime,
		],
	);

	const handleEventDragStart = useCallback(
		(e: React.MouseEvent<HTMLDivElement>, segment: EventSegment) => {
			if (!editable) return;

			e.preventDefault();
			e.stopPropagation();

			const startX = e.clientX;
			const startY = e.clientY;

			// Store pending drag info
			pendingDragRef.current = {
				segment,
				startX,
				startY,
				isDragActivated: false,
			};

			// Add temporary mousemove listener to check for drag threshold
			const pendingMoveListener = (moveEvent: MouseEvent) => {
				if (!pendingDragRef.current || pendingDragRef.current.isDragActivated) {
					return;
				}

				const deltaX = moveEvent.clientX - startX;
				const deltaY = moveEvent.clientY - startY;
				const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

				if (distance > DRAG_THRESHOLD) {
					// Threshold exceeded, activate drag
					pendingDragRef.current.isDragActivated = true;
					activateDrag(segment, startX, startY);
					document.removeEventListener("mousemove", pendingMoveListener);
					document.removeEventListener("mouseup", pendingUpListener);
				}
			};

			// Add temporary mouseup listener to cancel pending drag
			const pendingUpListener = (upEvent: MouseEvent) => {
				if (pendingDragRef.current && !pendingDragRef.current.isDragActivated) {
					// Drag was not activated, this is just a click
					pendingDragRef.current = null;
				}
				document.removeEventListener("mousemove", pendingMoveListener);
				document.removeEventListener("mouseup", pendingUpListener);
			};

			document.addEventListener("mousemove", pendingMoveListener);
			document.addEventListener("mouseup", pendingUpListener);
		},
		[editable, activateDrag],
	);

	const handleDragEndInternal = useCallback(() => {
		draggedEventRef.current = null;
		pendingDragRef.current = null;
		shiftKeyPressedRef.current = false;
		setIsFreeDragMode(false);
		setDragPosition(null);
		setHoveredDropDate(null);
		updateDropLineInfo(null);

		if (dragListenerRef.current) {
			document.removeEventListener("mousemove", dragListenerRef.current);
			dragListenerRef.current = null;
		}
		if (dragEndListenerRef.current) {
			document.removeEventListener("mouseup", dragEndListenerRef.current);
			dragEndListenerRef.current = null;
		}
		if (escapeListenerRef.current) {
			document.removeEventListener("keydown", escapeListenerRef.current);
			escapeListenerRef.current = null;
		}
	}, [updateDropLineInfo]);

	const handleDayMouseOver = useCallback(() => {
		if (draggedEventRef.current) {
			const dayElement = document.elementFromPoint(
				dragPosition?.x || 0,
				dragPosition?.y || 0,
			) as HTMLElement;

			let element = dayElement;
			while (element && !element.hasAttribute("data-date")) {
				element = element.parentElement as HTMLElement;
			}
			if (element) {
				const dateStr = element.getAttribute("data-date");
				if (dateStr) {
					try {
						const serverTz = TimeService.getInstance().getServerTimeZone();
						const hoveredMoment = moment
							.tz(dateStr, "YYYY-MM-DD", serverTz)
							.startOf("day");
						setHoveredDropDate(hoveredMoment.toDate());
					} catch {}
				}
			}
		}
	}, [dragPosition]);

	const isDraggingEvent = draggedEventRef.current !== null;

	const previewDates = useMemo(() => {
		if (!draggedEventRef.current || !dropLineInfo) return null;

		const { segment } = draggedEventRef.current;
		const duration =
			segment.originalEnd.getTime() - segment.originalStart.getTime();
		const serverTz = TimeService.getInstance().getServerTimeZone();

		try {
			if (dropLineInfo.position === "before" && dropLineInfo.targetEventId) {
				const targetSegment = eventSegments.find(
					(seg) => seg.event.EventId === dropLineInfo.targetEventId,
				);

				if (targetSegment) {
					const newEnd = moment.tz(targetSegment.segStart, serverTz).toDate();
					const newStart = new Date(newEnd.getTime() - duration);
					return { start: newStart, end: newEnd };
				}
			} else if (
				dropLineInfo.position === "after" &&
				dropLineInfo.targetEventId
			) {
				const targetSegment = eventSegments.find(
					(seg) => seg.event.EventId === dropLineInfo.targetEventId,
				);

				if (targetSegment) {
					// Check if we should use START or END of target event
					const useStart = dropLineInfo.useStartInsteadOfEnd ?? false;
					const newStart = useStart
						? moment.tz(targetSegment.segStart, serverTz).toDate()
						: moment.tz(targetSegment.segEnd, serverTz).toDate();
					const newEnd = new Date(newStart.getTime() + duration);
					return { start: newStart, end: newEnd };
				}
			} else if (
				dropLineInfo.position === "empty" &&
				dropLineInfo.targetHour !== undefined &&
				hoveredDropDate
			) {
				const startMoment = moment.tz(hoveredDropDate, serverTz);
				startMoment
					.hours(dropLineInfo.targetHour)
					.minutes(0)
					.seconds(0)
					.milliseconds(0);
				const newStart = startMoment.toDate();
				const newEnd = new Date(newStart.getTime() + duration);
				return { start: newStart, end: newEnd };
			} else if (hoveredDropDate) {
				return calculateNewEventDates(
					segment.originalStart,
					segment.originalEnd,
					hoveredDropDate,
				);
			}
		} catch (error) {
			console.error("Error calculating preview dates:", error);
		}

		return null;
	}, [draggedEventRef.current, dropLineInfo, hoveredDropDate, eventSegments]);

	return {
		handleEventDragStart,
		handleDayMouseOver,
		isDraggingEvent,
		draggedEvent: draggedEventRef.current,
		dragPosition,
		hoveredDropDate,
		dropLineInfo,
		previewDates,
	};
};
